/**
 * Work Session Service Base Class
 * Platform-agnostic implementation - no API client imports!
 * Both web and mobile versions instantiate this with their respective API clients
 */
export class WorkSessionService {
    constructor(client) {
        this.apiClient = client;
    }
    /**
     * Get the currently active work session for the authenticated user
     * @returns The active session or null if not clocked in
     */
    async getActiveSession() {
        const response = await this.apiClient.get('/work-sessions/active');
        // Handle 404 as "no active session" (not an error)
        if (!response.success && response.error?.includes('404')) {
            return { success: true, data: null };
        }
        // Handle "no active session" error message gracefully
        if (!response.success && response.error?.toLowerCase().includes('no active')) {
            return { success: true, data: null };
        }
        return response;
    }
    /**
     * Check in. ONE check-in covers the whole day: walking to a sister shop
     * needs no second punch and there is no move — the server has no transfer
     * endpoint, and this service deliberately has no method for one.
     *
     * Send GPS. The server matches it against the fences of EVERY location the
     * person is assigned to, passes on the first match and records that
     * location itself. `location_id` is a hint the server may use when it
     * genuinely knows better; a fence match wins over it either way.
     *
     * Refusals: 409 OPEN_SESSION_EXISTS (already checked in — their time is
     * already running); 403 GEOFENCE_VIOLATION (at none of their locations) /
     * GEOFENCE_GPS_REQUIRED; 400 LOCATION_INVALID / LOCATION_REQUIRED (only
     * when they belong to no location at all); 423 PERIOD_LOCKED.
     */
    async startSession(request) {
        return this.apiClient.post('/work-sessions/start', request || {});
    }
    /**
     * End the current active work session (clock out)
     * @param request Optional request body with notes
     * @returns The completed work session
     */
    async endSession(request) {
        const response = await this.apiClient.post('/work-sessions/end', request || {});
        return response;
    }
    /**
     * List a user's sessions in a window (self, or work_sessions:read for
     * others — enforced server-side). from/to are RFC3339 instants.
     */
    async listForUser(userId, from, to, limit = 200) {
        const params = { limit };
        if (from)
            params.from = from;
        if (to)
            params.to = to;
        return this.apiClient.get(`/users/${userId}/sessions`, params);
    }
}
